import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import {
  Search, X, Pencil, Trash2, Upload, Download, FolderKanban, FileText,
  CheckCircle2, Clock3, XCircle, GitCompareArrows, Loader2, FileDown, Save, Info, ChevronDown, Columns3, Receipt,
} from "lucide-react";
import { api, apiErrorMessage, API_URL } from "@/lib/api";
import { FICHES, DOC_TYPES, REQUIRED_DOC_TYPES, STATUTS, STATUT_INFOS, PRIX_CLIENT_MAX, fmt, euros, VERSEMENT_PRIME_OPTIONS, VERSEMENT_PRIME_LABELS } from "@/lib/cee";
import { useAuth } from "@/context/AuthContext";
import { FadeIn } from "@/components/Reveal";

const STATUT_STYLES = {
  "En création": "bg-slate-500/10 text-[var(--dim)] border-[var(--line)]",
  "Devis envoyé": "bg-blue-500/12 text-[var(--blue-text)] border-blue-500/30",
  "Devis validé": "bg-violet-500/12 text-[var(--violet-text)] border-violet-500/30",
  "En instruction": "bg-amber-500/12 text-[var(--amber-text)] border-amber-500/30",
  Validé: "bg-emerald-500/12 text-[var(--green-text)] border-emerald-500/30",
  Réglé: "bg-cyan-500/12 text-[var(--cyan-text)] border-cyan-500/30",
  "Mise en conformité": "bg-rose-500/12 text-[var(--rose-text)] border-rose-500/30",
};

const StatusBadge = ({ statut }) => (
  <span
    data-testid="dossier-statut-badge"
    title={STATUT_INFOS[statut] || statut}
    className={`inline-flex whitespace-nowrap rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${STATUT_STYLES[statut] || STATUT_STYLES["En création"]}`}
  >
    {statut}
  </span>
);

/* Colonnes masquables du tableau (Nom du dossier et Actions toujours affichées) */
const ADMIN_ONLY_COLS = ["prixBenef", "prixComm"];
const TABLE_COLUMNS = [
  { key: "reference", label: "Référence" },
  { key: "devisNum", label: "Réf. devis" },
  { key: "statut", label: "Statut" },
  { key: "versement", label: "Versement" },
  { key: "adresse", label: "Adresse des travaux" },
  { key: "date", label: "Date" },
  { key: "operations", label: "Opérations" },
  { key: "puissance", label: "Puissance (kW)" },
  { key: "kwh", label: "kWh cumac" },
  { key: "prixBenef", label: "Prix bénéficiaire (€/MWhc)", adminOnly: true },
  { key: "prixComm", label: "Prix commission (€/MWhc)", adminOnly: true },
  { key: "commission", label: "Commission" },
  { key: "prime", label: "Prime CEE" },
  { key: "commentaire", label: "Commentaire" },
  { key: "documents", label: "Documents" },
];

const COLUMN_LABELS = Object.fromEntries(TABLE_COLUMNS.map((c) => [c.key, c.label]));

/* Code couleur du badge documents — rouge : pièce refusée (à redéposer) · orange : en attente de
   validation admin · vert : tout est validé · bleu : en attente d'un document du partenaire */
const docsBadgeMeta = ({ refused = 0, pending = false, validated = false, refusedTypes = [] }) => {
  if (refused > 0)
    return {
      cls: "border-rose-500/40 bg-rose-500/10 text-[var(--rose-text)]",
      title: `${refused} document${refused > 1 ? "s" : ""} refusé${refused > 1 ? "s" : ""}${refusedTypes.length ? ` (${refusedTypes.join(", ")})` : ""} — à redéposer après correction`,
    };
  if (pending) return { cls: "border-amber-500/40 bg-amber-500/10 text-[var(--amber-text)]", title: "Un document est en attente de validation" };
  if (validated) return { cls: "border-emerald-500/40 bg-emerald-500/10 text-[var(--green-text)]", title: "Tous les documents obligatoires sont validés" };
  return { cls: "border-blue-500/40 bg-blue-500/10 text-[var(--blue-text)]", title: "En attente de documents du partenaire — à déposer sur le portail" };
};

/* Cellules de rendu par colonne — l'ordre d'affichage suit la préférence de l'utilisateur */
const COLUMN_CELLS = {
  reference: (d, t) => <td data-testid={`dossier-reference-${d.id}`} className="whitespace-nowrap px-5 py-4 font-mono text-xs font-semibold text-[var(--accent-text)]">{d.reference || "—"}</td>,
  devisNum: (d, t) => <td className="whitespace-nowrap px-4 py-4 font-mono text-xs text-[var(--dim)]">{d.devisNum || "—"}</td>,
  statut: (d, t) => <td className="px-4 py-4"><StatusBadge statut={d.statut} /></td>,
  versement: (d, t) => (
    <td data-testid={`dossier-versement-${d.id}`} className="whitespace-nowrap px-4 py-4 text-xs text-[var(--dim)]">
      {d.versementPrime ? VERSEMENT_PRIME_LABELS[d.versementPrime] : <span className="italic text-[var(--mute)]">À définir</span>}
    </td>
  ),
  adresse: (d, t) => <td className="max-w-[220px] truncate px-4 py-4 text-xs text-[var(--dim)]">{d.adresseTravaux || "—"}</td>,
  date: (d, t) => <td className="whitespace-nowrap px-4 py-4 font-mono text-xs text-[var(--dim)]">{(d.created_at || "").slice(0, 10).split("-").reverse().join("/")}</td>,
  operations: (d, t) => <td className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--dim)]">{t.nbOperations ?? "—"}</td>,
  puissance: (d, t) => <td className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--dim)]">{fmt(t.puissanceTotale, 1)}</td>,
  kwh: (d, t) => <td className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--dim)]">{fmt(Math.round(t.kwhCumacTotal || 0))}</td>,
  prixBenef: (d, t) => <td data-testid={`dossier-prixBenef-${d.id}`} className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--dim)]">{fmt(t.prixCession ?? PRIX_CLIENT_MAX, 2)} €</td>,
  prixComm: (d, t) => <td data-testid={`dossier-prixComm-${d.id}`} className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--amber-text)]">{fmt(Math.max(0, PRIX_CLIENT_MAX - (t.prixCession ?? PRIX_CLIENT_MAX)), 2)} €</td>,
  commission: (d, t) => <td className="whitespace-nowrap px-4 py-4 text-right font-mono text-[var(--amber-text)]">{euros(Math.round(t.commissionTotal || 0))}</td>,
  prime: (d, t) => <td className="whitespace-nowrap px-4 py-4 text-right font-mono font-semibold text-[var(--accent-text)]">{euros(Math.round(t.primeNette || 0))}</td>,
  commentaire: (d, t) => <td className="max-w-[180px] truncate px-4 py-4 text-xs text-[var(--mute)]">{d.commentaire || "—"}</td>,
  documents: (d, t) => {
    const meta = docsBadgeMeta({
      refused: d.docsRefused || 0,
      pending: !!d.docsPending,
      validated: (d.docsValidated || 0) >= REQUIRED_DOC_TYPES.length,
      refusedTypes: d.docsRefusedTypes || [],
    });
    return (
      <td className="px-4 py-4">
        <span
          data-testid={`dossier-docs-badge-${d.id}`}
          title={meta.title}
          className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 font-mono text-[10px] ${meta.cls}`}
        >
          {(d.docsDistinct || 0)}/{REQUIRED_DOC_TYPES.length}
        </span>
      </td>
    );
  },
};

/* Valeurs de tri par colonne (clic sur l'en-tête) */
const SORT_VALUE = {
  nom: (d) => (d.nom || "").toLowerCase(),
  reference: (d) => d.reference || "",
  devisNum: (d) => d.devisNum || "",
  statut: (d) => d.statut || "",
  versement: (d) => d.versementPrime || "",
  adresse: (d) => (d.adresseTravaux || "").toLowerCase(),
  date: (d) => d.created_at || "",
  operations: (d) => d.totals?.nbOperations ?? -1,
  puissance: (d) => d.totals?.puissanceTotale ?? -1,
  kwh: (d) => d.totals?.kwhCumacTotal ?? -1,
  prixBenef: (d) => d.totals?.prixCession ?? -1,
  prixComm: (d) => Math.max(0, PRIX_CLIENT_MAX - (d.totals?.prixCession ?? PRIX_CLIENT_MAX)),
  commission: (d) => d.totals?.commissionTotal ?? -1,
  prime: (d) => d.totals?.primeNette ?? -1,
  commentaire: (d) => (d.commentaire || "").toLowerCase(),
  documents: (d) => d.docsDistinct || 0,
};

const emptyImg =
  "https://images.unsplash.com/photo-1496247749665-49cf5b1022e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200&auto=format&fit=crop";

const Dossiers = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [dossiers, setDossiers] = useState(null);
  const [query, setQuery] = useState("");
  const [statutFilter, setStatutFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState("");
  const [uploading, setUploading] = useState(false);
  const [commentaireDraft, setCommentaireDraft] = useState("");
  const [obligeDraft, setObligeDraft] = useState("");
  const [cols, setCols] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dossiers-cols"));
      if (Array.isArray(saved)) {
        // Ordre sauvegardé préservé ; les colonnes masquées le restent.
        let valid = saved.filter((k) => TABLE_COLUMNS.some((c) => c.key === k));
        // Colonne « Référence » (nouvelle) : insérée en tête une seule fois pour les préférences existantes
        if (localStorage.getItem("dossiers-ref-inseree") !== "1") {
          if (!valid.includes("reference")) valid = ["reference", ...valid];
          localStorage.setItem("dossiers-ref-inseree", "1");
        }
        // Colonne « Versement » (nouvelle) : insérée une seule fois après « Statut »
        if (localStorage.getItem("dossiers-versement-inseree") !== "1") {
          if (!valid.includes("versement")) {
            const i = valid.indexOf("statut");
            valid.splice(i === -1 ? valid.length : i + 1, 0, "versement");
          }
          localStorage.setItem("dossiers-versement-inseree", "1");
        }
        return [...valid];
      }
    } catch { /* préférences illisibles : toutes les colonnes */ }
    return TABLE_COLUMNS.map((c) => c.key);
  });
  const [colsOpen, setColsOpen] = useState(false);
  const [hideRegle, setHideRegle] = useState(() => localStorage.getItem("dossiers-hide-regle") === "1");

  // Prix unitaires (bénéficiaire / commission) réservés à l'administrateur
  const visibleCols = isAdmin ? cols : cols.filter((k) => !ADMIN_ONLY_COLS.includes(k));

  const dragCol = useRef(null);
  const [dragOverCol, setDragOverCol] = useState(null);

  const moveCol = (fromKey, toKey) => {
    if (!fromKey || !toKey || fromKey === toKey) return;
    setCols((prev) => {
      const from = prev.indexOf(fromKey);
      const to = prev.indexOf(toKey);
      if (from === -1 || to === -1) return prev;
      const next = [...prev];
      next.splice(from, 1);
      next.splice(to, 0, fromKey);
      localStorage.setItem("dossiers-cols", JSON.stringify(next));
      return next;
    });
  };

  const toggleCol = (key) => {
    setCols((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      localStorage.setItem("dossiers-cols", JSON.stringify(next));
      return next;
    });
  };

  const load = () => api.get("/dossiers").then(({ data }) => setDossiers(data)).catch((e) => toast.error(apiErrorMessage(e)));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!dossiers) return [];
    return dossiers.filter((d) => {
      const okStatut = (!statutFilter || d.statut === statutFilter) && (!hideRegle || d.statut !== "Réglé");
      const q = query.trim().toLowerCase();
      const okQuery =
        !q ||
        (d.nom || "").toLowerCase().includes(q) ||
        (d.clientNom || "").toLowerCase().includes(q) ||
        (d.adresseTravaux || "").toLowerCase().includes(q);
      return okStatut && okQuery;
    });
  }, [dossiers, query, statutFilter, hideRegle]);

  // Totaux des dossiers filtrés (affichés en bas de tableau)
  const sumKwh = (filtered || []).reduce((a, d) => a + (d.totals?.kwhCumacTotal || 0), 0);
  const sumCommission = (filtered || []).reduce((a, d) => a + (d.totals?.commissionTotal || 0), 0);
  const sumPrime = (filtered || []).reduce((a, d) => a + (d.totals?.primeNette || 0), 0);

  // Tri par clic sur l'en-tête : décroissant → croissant → neutre (mémorisé d'une session à l'autre)
  const [tri, setTri] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("dossiers-tri"));
      if (saved && SORT_VALUE[saved.key]) return { key: saved.key, dir: saved.dir === 1 ? 1 : -1 };
    } catch { /* préférence illisible : pas de tri */ }
    return { key: null, dir: -1 };
  });
  const tries = useMemo(() => {
    if (!tri.key || !SORT_VALUE[tri.key]) return filtered;
    const val = SORT_VALUE[tri.key];
    return [...filtered].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === "string" || typeof vb === "string")
        return String(va ?? "").localeCompare(String(vb ?? ""), "fr") * tri.dir;
      return ((va ?? 0) - (vb ?? 0)) * tri.dir;
    });
  }, [filtered, tri]);

  const clicTri = (key) => setTri((t) => {
    const next = t.key !== key ? { key, dir: -1 } : (t.dir === -1 ? { key, dir: 1 } : { key: null, dir: -1 });
    localStorage.setItem("dossiers-tri", JSON.stringify(next));
    return next;
  });

  const openDrawer = (d) => {
    setOpenId(d.id);
    setCommentaireDraft(d.commentaire || "");
    setObligeDraft(d.oblige || "");
    api.get(`/dossiers/${d.id}/documents`).then(({ data }) => setDocs(data)).catch(() => setDocs([]));
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    if (!docType) {
      toast.error("Sélectionnez d'abord un type de document.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("docType", docType);
      await api.post(`/dossiers/${openId}/documents`, fd);
      toast.success("Document téléversé — en attente de validation.");
      setDocType("");
      const { data } = await api.get(`/dossiers/${openId}/documents`);
      setDocs(data);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const setDocStatus = async (docId, status) => {
    const statusComment = status === "refuse" ? window.prompt("Motif du refus (optionnel) :", "") || "" : "";
    try {
      await api.put(`/documents/${docId}`, { status, statusComment });
      const { data } = await api.get(`/dossiers/${openId}/documents`);
      setDocs(data);
      toast.success(status === "valide" ? "Document validé." : "Document refusé.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    await api.delete(`/documents/${docId}`).catch(() => {});
    const { data } = await api.get(`/dossiers/${openId}/documents`);
    setDocs(data);
    load();
  };

  /* Corps d'une requête PUT /dossiers — inclut toujours le mode de versement de la prime */
  const dossierPayload = (d, patch = {}) => ({
    nom: d.nom, statut: d.statut, adresseTravaux: d.adresseTravaux, benefSiren: d.benefSiren,
    clientNom: d.clientNom, benefAdresse: d.benefAdresse, devisNum: d.devisNum || "",
    devisValidite: d.devisValidite || "30", entTel: d.entTel || "", entEmail: d.entEmail || "",
    commentaire: d.commentaire || "",
    prixOverride: d.prixOverride, prixMwh: d.prixMwh, prixCession: d.prixCession || 6,
    versementPrime: d.versementPrime || "",
    operations: (d.operations || []).map(({ fiche, marque, reference, application, puissance, nbMoteurs, kwhCumac }) => ({
      fiche, marque, reference, application, puissance, nbMoteurs, kwhCumac: kwhCumac ?? null,
    })),
    ...patch,
  });

  const changeStatut = async (d, statut, commentaire = null) => {
    try {
      await api.put(`/dossiers/${d.id}`, dossierPayload(d, {
        statut,
        commentaire: commentaire !== null ? commentaire : d.commentaire || "",
      }));
      if (commentaire === null) toast.success(`Statut : ${statut}.`);
      load();
      setOpenId((id) => (id === d.id ? null : id));
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const saveCommentaire = async (d, commentaire) => {
    try {
      await api.put(`/dossiers/${d.id}`, dossierPayload(d, { commentaire }));
      toast.success("Commentaire enregistré.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const setVersement = async (d, versementPrime) => {
    try {
      await api.put(`/dossiers/${d.id}`, dossierPayload(d, { versementPrime }));
      toast.success(`Versement de la prime : ${VERSEMENT_PRIME_LABELS[versementPrime]}.`);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const saveOblige = async (d) => {
    try {
      await api.put(`/dossiers/${d.id}`, dossierPayload(d, { oblige: obligeDraft.trim() }));
      toast.success("Obligé enregistré.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const deleteDossier = async (d) => {
    if (!window.confirm(`Supprimer définitivement le dossier « ${d.nom} » ?`)) return;
    try {
      await api.delete(`/dossiers/${d.id}`);
      toast.success("Dossier supprimé.");
      setOpenId(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const addToComparateur = async (d) => {
    try {
      await api.post("/simulations", {
        nom: d.clientNom || d.nom,
        prixOverride: d.prixOverride, prixMwh: d.prixMwh, prixCession: d.prixCession || 6,
        operations: (d.operations || []).map(({ fiche, marque, reference, application, puissance, nbMoteurs }) => ({
          fiche, marque, reference, application, puissance, nbMoteurs,
        })),
      });
      toast.success("Simulation ajoutée au comparateur.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  /* Valeurs CSV par colonne — l'export reflète exactement l'écran (colonnes visibles, dans leur ordre, triées) */
  const COLUMN_CSV = {
    nom: (d) => d.nom || "",
    reference: (d) => d.reference || "",
    devisNum: (d) => d.devisNum || "",
    statut: (d) => d.statut || "",
    versement: (d) => (d.versementPrime ? VERSEMENT_PRIME_LABELS[d.versementPrime] : "À définir"),
    adresse: (d) => d.adresseTravaux || "",
    date: (d) => (d.created_at || "").slice(0, 10).split("-").reverse().join("/"),
    operations: (d) => d.totals?.nbOperations ?? "",
    puissance: (d) => fmt(d.totals?.puissanceTotale, 1),
    kwh: (d) => fmt(Math.round(d.totals?.kwhCumacTotal || 0)),
    prixBenef: (d) => fmt(d.totals?.prixCession ?? PRIX_CLIENT_MAX, 2),
    prixComm: (d) => fmt(Math.max(0, PRIX_CLIENT_MAX - (d.totals?.prixCession ?? PRIX_CLIENT_MAX)), 2),
    commission: (d) => Math.round(d.totals?.commissionTotal || 0),
    prime: (d) => Math.round(d.totals?.primeNette || 0),
    commentaire: (d) => d.commentaire || "",
    documents: (d) => `${d.docsDistinct || 0}/${REQUIRED_DOC_TYPES.length}`,
  };

  const exportCsv = () => {
    const colonnes = ["nom", ...visibleCols];
    const rows = [
      ["Nom du dossier", ...colonnes.slice(1).map((k) => COLUMN_LABELS[k])],
      ...tries.map((d) => colonnes.map((k) => (COLUMN_CSV[k] || (() => ""))(d))),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? "").replaceAll('"', '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `dossiers-cee-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success(`Export CSV téléchargé (${tries.length} dossiers, colonnes de l'écran).`);
  };

  const openDossier = dossiers?.find((x) => x.id === openId);

  const docStatusMeta = {
    en_attente: { icon: Clock3, cls: "text-[var(--amber-text)]", label: "En attente de validation" },
    valide: { icon: CheckCircle2, cls: "text-[var(--accent-text)]", label: "Validé" },
    refuse: { icon: XCircle, cls: "text-[var(--rose-text)]", label: "Refusé" },
  };

  return (
    <div className="mx-auto max-w-[1440px] px-4 pb-24 sm:px-6">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-[11px] font-mono uppercase tracking-[0.24em] text-[var(--accent-text)]">Suivi de vos dossiers</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">Liste des dossiers</h1>
        </div>
        <button data-testid="dossiers-export-csv-btn" onClick={exportCsv} className="btn-ghost">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {/* Filtres */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-80">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--mute)]" />
          <input
            data-testid="dossiers-search-input"
            className="field-input pl-10"
            placeholder="Rechercher un dossier, un bénéficiaire, une adresse…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <select data-testid="dossiers-statut-filter" className="field-input w-auto" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
          <option value="">Tous les statuts</option>
          {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label data-testid="dossiers-hide-regle-label" className="flex cursor-pointer select-none items-center gap-2 text-xs text-[var(--dim)]">
          <input
            type="checkbox"
            data-testid="dossiers-hide-regle"
            checked={hideRegle}
            onChange={(e) => { setHideRegle(e.target.checked); localStorage.setItem("dossiers-hide-regle", e.target.checked ? "1" : "0"); }}
            className="h-4 w-4 accent-[var(--brand)]"
          />
          Masquer les dossiers réglés
        </label>
        <span className="ml-auto text-xs font-mono text-[var(--mute)]">{filtered.length} dossier(s)</span>
        <div className="relative">
          <button data-testid="dossiers-columns-btn" onClick={() => setColsOpen((o) => !o)} className="btn-ghost h-9 px-3 text-xs">
            <Columns3 size={14} /> Colonnes
          </button>
          {colsOpen && (
            <>
              <div className="fixed inset-0 z-40 cursor-default" onClick={() => setColsOpen(false)} />
              <div data-testid="dossiers-columns-popover" className="absolute right-0 z-50 mt-2 w-60 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2 shadow-2xl">
                <p className="px-2 pb-1.5 pt-1 text-[10px] font-mono uppercase tracking-widest text-[var(--mute)]">Colonnes affichées</p>
                {TABLE_COLUMNS.filter((c) => isAdmin || !c.adminOnly).map((c) => (
                  <label key={c.key} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-[var(--dim)] transition-colors hover:bg-[var(--ghost-bg)] hover:text-[var(--ink)]">
                    <input
                      type="checkbox"
                      data-testid={`dossiers-col-${c.key}`}
                      checked={visibleCols.includes(c.key)}
                      onChange={() => toggleCol(c.key)}
                      className="h-4 w-4 accent-[var(--brand)]"
                    />
                    {c.label}
                  </label>
                ))}
                <p className="px-2 pb-1 pt-2 text-[10px] leading-relaxed text-[var(--mute)]">
                  Astuce : cliquez sur un en-tête pour trier, glissez-déposez-le pour réordonner les colonnes.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Légende des statuts */}
      <details className="card-surface group mb-6 px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
        <summary
          data-testid="statuts-legend-toggle"
          className="flex cursor-pointer select-none items-center gap-2 text-[12px] font-medium text-[var(--dim)] transition-colors hover:text-[var(--ink)]"
        >
          <Info size={14} className="text-[var(--accent-text)]" />
          Signification des statuts
          <ChevronDown size={14} className="transition-transform duration-200 group-open:rotate-180" />
        </summary>
        <div className="mt-4 grid gap-x-8 gap-y-2.5 border-t border-[var(--line-soft)] pt-4 text-[12px] leading-relaxed sm:grid-cols-2">
          {Object.entries(STATUT_INFOS).map(([s, txt]) => (
            <div key={s} className="flex items-start gap-2.5">
              <StatusBadge statut={s} />
              <span className="text-[var(--mute)]">{txt}</span>
            </div>
          ))}
        </div>
      </details>

      {dossiers === null ? (
        <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[var(--accent-text)]" /></div>
      ) : filtered.length === 0 ? (
        <FadeIn className="card-surface relative overflow-hidden p-10 text-center">
          <img src={emptyImg} alt="" className="absolute inset-0 h-full w-full object-cover opacity-8" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#090D14] via-[#090D14]/85 to-[#090D14]/70" />
          <div className="relative">
            <FolderKanban size={38} className="mx-auto mb-4 text-[var(--accent-text)]" />
            <h2 className="font-display text-xl font-bold text-[var(--ink)]">
              {dossiers.length === 0 ? "Aucun dossier enregistré pour l'instant." : "Aucun dossier ne correspond à cette recherche."}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-[var(--dim)]">
              Utilisez le calculateur pour estimer une prime CEE, puis cliquez sur « Enregistrer le dossier » — il apparaîtra ici.
            </p>
            <button data-testid="dossiers-empty-cta" onClick={() => navigate("/calculateur")} className="btn-primary mt-6">
              Ouvrir le calculateur
            </button>
          </div>
        </FadeIn>
      ) : (
        <>
          {/* Table desktop — colonnes du cahier initial */}
          <div className="card-surface hidden overflow-x-auto md:block">
            <table className="w-full text-sm" style={{ minWidth: 200 + visibleCols.length * 96 }}>
              <thead>
                <tr className="border-b border-[var(--line)] text-left text-[10px] font-mono uppercase tracking-widest text-[var(--mute)]">
                  <th
                    data-testid="dossier-col-nom"
                    onClick={() => clicTri("nom")}
                    className="cursor-pointer select-none hover:text-[var(--accent-text)]"
                  >
                    Nom du dossier
                    <span data-testid={`dossiers-sort-arrow-nom`}>{tri.key === "nom" ? (tri.dir === 1 ? " ▲" : " ▼") : ""}</span>
                  </th>
                  {visibleCols.map((key) => (
                    <th
                      key={key}
                      data-testid={`dossier-col-${key}`}
                      draggable
                      title="Cliquez pour trier, glissez pour déplacer"
                      onClick={() => clicTri(key)}
                      onDragStart={(e) => { dragCol.current = key; e.dataTransfer.effectAllowed = "move"; }}
                      onDragOver={(e) => { e.preventDefault(); if (dragOverCol !== key) setDragOverCol(key); }}
                      onDragLeave={() => setDragOverCol((c) => (c === key ? null : c))}
                      onDrop={(e) => { e.preventDefault(); moveCol(dragCol.current, key); setDragOverCol(null); }}
                      onDragEnd={() => setDragOverCol(null)}
                      className={`cursor-pointer select-none active:cursor-grabbing hover:text-[var(--accent-text)]${dragOverCol === key ? " bg-sky-500/10 text-[var(--accent-text)]" : ""}`}
                    >
                      {COLUMN_LABELS[key]}
                      <span data-testid={`dossiers-sort-arrow-${key}`}>{tri.key === key ? (tri.dir === 1 ? " ▲" : " ▼") : ""}</span>
                    </th>
                  ))}
                  <th className="px-5 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {tries.map((d) => {
                  const t = d.totals || {};
                  return (
                    <tr
                      key={d.id}
                      data-testid={`dossier-row-${d.id}`}
                      onClick={() => openDrawer(d)}
                      className="cursor-pointer border-b border-[var(--line-soft)] transition-colors last:border-0 hover:bg-sky-500/5"
                    >
                      <td className="px-5 py-4 font-medium text-[var(--ink)]">{d.nom}</td>
                      {visibleCols.map((key) => (
                        <React.Fragment key={key}>{COLUMN_CELLS[key](d, t)}</React.Fragment>
                      ))}
                      <td className="px-5 py-4">
                        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                          <button data-testid={`dossier-docs-btn-${d.id}`} title="Déposer des documents" onClick={() => openDrawer(d)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-amber-500/40 hover:text-[var(--amber-text)]">
                            <Upload size={13} />
                          </button>
                          <button data-testid={`dossier-edit-btn-${d.id}`} title="Modifier dans le calculateur"
                            onClick={() => navigate(`/calculateur?edit=${d.id}`)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-sky-500/40 hover:text-[var(--accent-text)]">
                            <Pencil size={13} />
                          </button>
                          <button data-testid={`dossier-delete-btn-${d.id}`} title="Supprimer"
                            onClick={() => deleteDossier(d)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-rose-500/40 hover:text-[var(--rose-text)]">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr data-testid="dossiers-total-row" className="border-t-2 border-[var(--line)] bg-[var(--panel-2)] font-mono text-[11px] font-bold">
                  {(() => {
                    const firstTotalIdx = visibleCols.findIndex((k) => ["kwh", "commission", "prime"].includes(k));
                    return (
                      <>
                        <td
                          colSpan={firstTotalIdx === -1 ? 1 + visibleCols.length : 1 + firstTotalIdx}
                          className="px-5 py-3.5 text-[var(--dim)]"
                        >
                          Total — {tries.length} dossier{tries.length > 1 ? "s" : ""}
                        </td>
                        {firstTotalIdx >= 0 && visibleCols.slice(firstTotalIdx).map((k) => (
                          <td key={k} className="px-4 py-3.5 text-right">
                            {k === "kwh" && <span data-testid="dossiers-total-kwh" className="text-[var(--dim)]">{fmt(Math.round(sumKwh))}</span>}
                            {k === "commission" && <span data-testid="dossiers-total-commission" className="text-[var(--amber-text)]">{euros(Math.round(sumCommission))}</span>}
                            {k === "prime" && <span data-testid="dossiers-total-prime" className="text-[var(--accent-text)]">{euros(Math.round(sumPrime))}</span>}
                          </td>
                        ))}
                      </>
                    );
                  })()}
                  <td />
                </tr>
              </tfoot>
            </table>
            <p className="border-t border-[var(--line-soft)] px-5 py-3 text-center text-[10px] font-mono uppercase tracking-widest text-[var(--mute)] md:hidden">
              Faites glisser le tableau horizontalement pour voir toutes les colonnes
            </p>
          </div>

          {/* Cartes mobile */}
          <div className="space-y-3 md:hidden">
            {tries.map((d) => {
              const t = d.totals || {};
              const badgeDocs = docsBadgeMeta({
                refused: d.docsRefused || 0,
                pending: !!d.docsPending,
                validated: (d.docsValidated || 0) >= REQUIRED_DOC_TYPES.length,
                refusedTypes: d.docsRefusedTypes || [],
              });
              return (
                <button key={d.id} data-testid={`dossier-card-${d.id}`} onClick={() => openDrawer(d)} className="card-surface block w-full p-4 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-[var(--ink)]">{d.nom}</span>
                    <StatusBadge statut={d.statut} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-[var(--mute)]">
                    {(d.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                    {d.devisNum ? ` · ${d.devisNum}` : ""} ·
                    <span
                      title={badgeDocs.title}
                      className={`inline-flex whitespace-nowrap rounded-full border px-1.5 py-0 font-mono text-[10px] ${badgeDocs.cls}`}
                    >
                      {(d.docsDistinct || 0)}/{REQUIRED_DOC_TYPES.length}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2 font-mono text-sm whitespace-nowrap">
                    <span className="text-[var(--dim)]">
                      {fmt(t.mwhCumacTotal, 1)} MWh · comm. {euros(Math.round(t.commissionTotal || 0))}
                    </span>
                    <span className="font-semibold text-[var(--accent-text)]">{euros(Math.round(t.primeNette || 0))}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* ------- Panneau dossier ------- */}
      <AnimatePresence>
        {openDossier && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
              onClick={() => setOpenId(null)}
            />
            <motion.aside
              data-testid="dossier-drawer"
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed inset-y-0 right-0 z-50 w-full max-w-xl overflow-y-auto border-l border-[var(--line)] bg-[var(--panel)] p-6 sm:p-8"
            >
              <div className="mb-6 flex items-start justify-between gap-3">
                <div>
                  <h2 data-testid="dossier-drawer-title" className="font-display text-2xl font-bold text-[var(--ink)]">{openDossier.nom}</h2>
                  <p className="mt-1 text-xs text-[var(--mute)]">
                    {openDossier.reference ? <span data-testid="dossier-drawer-reference" className="font-mono font-semibold text-[var(--accent-text)]">{openDossier.reference}</span> : null}
                    {openDossier.reference ? " — " : ""}
                    {(openDossier.created_at || "").slice(0, 10).split("-").reverse().join("/")}
                    {openDossier.adresseTravaux ? ` — ${openDossier.adresseTravaux}` : ""}
                  </p>
                </div>
                <button data-testid="dossier-drawer-close-btn" onClick={() => setOpenId(null)}
                  className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--line)] text-[var(--dim)] hover:text-[var(--ink)]">
                  <X size={15} />
                </button>
              </div>

              {/* Totaux */}
              <div className="mb-6 grid grid-cols-3 gap-3">
                {[
                  ["MWh cumac", fmt(openDossier.totals?.mwhCumacTotal, 1)],
                  ["Prix", `${fmt(openDossier.totals?.prixApplique, 1)} €/MWh`],
                  ["Prime nette", euros(Math.round(openDossier.totals?.primeNette || 0))],
                ].map(([l, v]) => (
                  <div key={l} className="whitespace-nowrap rounded-xl border border-[var(--line)] bg-[var(--inset)] p-3.5">
                    <div className="font-display text-lg font-bold text-[var(--ink)]">{v}</div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-[var(--mute)]">{l}</div>
                  </div>
                ))}
              </div>

              {/* Statut & commentaire */}
              <div className="mb-6 grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Statut du dossier</label>
                  {isAdmin ? (
                    <select
                      data-testid="dossier-statut-select"
                      className="field-input"
                      value={openDossier.statut}
                      onChange={(e) => changeStatut(openDossier, e.target.value)}
                    >
                      {STATUTS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <div data-testid="dossier-statut-readonly" className="flex min-h-[42px] items-center gap-2.5">
                      <StatusBadge statut={openDossier.statut} />
                      <span className="text-[11px] text-[var(--mute)]">Évolue automatiquement selon les documents déposés et validés.</span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Commentaire</label>
                  <div className="flex gap-2">
                    <input
                      data-testid="dossier-commentaire-input"
                      className="field-input"
                      placeholder="Note interne…"
                      value={commentaireDraft}
                      onChange={(e) => setCommentaireDraft(e.target.value)}
                    />
                    <button
                      data-testid="dossier-commentaire-save-btn"
                      onClick={() => saveCommentaire(openDossier, commentaireDraft)}
                      title="Enregistrer le commentaire"
                      className="btn-ghost shrink-0 px-3"
                    >
                      <Save size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Obligé de la filière CEE — renseigné par l'administrateur */}
              <div className="mb-6">
                <label className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Obligé de la filière CEE</label>
                {isAdmin ? (
                  <div className="flex gap-2">
                    <input
                      data-testid="dossier-oblige-input"
                      className="field-input"
                      placeholder="Ex. ENGIE, EDF Entreprises…"
                      value={obligeDraft}
                      onChange={(e) => setObligeDraft(e.target.value)}
                    />
                    <button
                      data-testid="dossier-oblige-save-btn"
                      onClick={() => saveOblige(openDossier)}
                      title="Enregistrer l'Obligé"
                      className="btn-ghost shrink-0 px-3"
                    >
                      <Save size={14} />
                    </button>
                  </div>
                ) : (
                  <div data-testid="dossier-oblige-readonly" className="flex min-h-[42px] items-center text-sm text-[var(--dim)]">
                    {openDossier.oblige || "À définir"}
                  </div>
                )}
                {!openDossier.oblige && (
                  <p className="mt-2 text-[11px] text-[var(--mute)]">Mention obligatoire de la facture — renseigné par l'administration.</p>
                )}
              </div>

              {/* Versement de la prime */}
              <div className="mb-6">
                <label className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">Versement de la prime CEE</label>
                <div className="grid gap-2 sm:grid-cols-2">
                  {VERSEMENT_PRIME_OPTIONS.map((o) => {
                    const selected = (openDossier.versementPrime || "") === o.value;
                    return (
                      <button
                        key={o.value}
                        data-testid={`drawer-versement-${o.value}`}
                        onClick={() => setVersement(openDossier, o.value)}
                        className={`rounded-xl border p-3.5 text-left transition-all ${
                          selected ? "border-sky-500/60 bg-sky-500/10" : "border-[var(--line)] bg-[var(--inset)] hover:border-[var(--line-hover)]"
                        }`}
                      >
                        <span className={`block text-[13px] font-semibold ${selected ? "text-[var(--accent-text)]" : "text-[var(--ink)]"}`}>{o.label}</span>
                        <span className="mt-1 block text-[11px] leading-relaxed text-[var(--mute)]">{o.hint}</span>
                      </button>
                    );
                  })}
                </div>
                {!openDossier.versementPrime && (
                  <p className="mt-2 text-[11px] text-[var(--mute)]">Mode à définir — il détermine à qui la prime est versée (appel à facturation « Prime CEE »).</p>
                )}
              </div>

              {/* Historique des statuts */}
              {(openDossier.historique || []).length > 0 && (
                <div className="mb-6">
                  <label className="mb-2 block text-[11px] font-mono uppercase tracking-widest text-[var(--dim)]">
                    Historique des statuts
                  </label>
                  <div className="space-y-2">
                    {[...(openDossier.historique || [])].reverse().map((h, i) => (
                      <div
                        key={i}
                        data-testid={`statut-history-item-${i}`}
                        className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-2.5 text-xs"
                      >
                        <StatusBadge statut={h.statut} />
                        <span className="font-mono text-[11px] text-[var(--mute)]">
                          {new Date(h.date).toLocaleDateString("fr-FR")}{" "}
                          {new Date(h.date).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })} · {h.auteur}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Opérations */}
              <h3 className="font-display mb-3 text-sm font-bold uppercase tracking-wide text-[var(--dim)]">Opérations ({(openDossier.operations || []).length})</h3>
              <div className="mb-6 space-y-2">
                {(openDossier.operations || []).map((op, i) => (
                  <div key={i} className="rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-xs text-[var(--accent-text)]">{op.fiche}{op.ficheVersion ? ` · ${op.ficheVersion}` : ""}</span>
                      <span className="text-xs text-[var(--mute)]">{APPLICATIONS_LABEL_SAFE(op)}</span>
                    </div>
                    <div className="mt-1 text-xs text-[var(--dim)]">
                      {[op.marque, op.reference].filter(Boolean).join(" · ") || "—"} · {fmt(op.puissance, 1)} kW
                    </div>
                  </div>
                ))}
              </div>

              {/* Documents */}
              <h3 className="font-display mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--dim)]">
                <FileText size={14} className="text-[var(--accent-text)]" /> Documents
                {(() => {
                  const reqState = REQUIRED_DOC_TYPES.map((t) => {
                    const ds = docs.filter((x) => x.docType === t && x.status !== "refuse");
                    return { missing: ds.length === 0, ok: ds.some((x) => x.status === "valide") };
                  });
                  const distinct = REQUIRED_DOC_TYPES.filter((t) =>
                    docs.some((x) => x.docType === t && x.status !== "refuse")
                  ).length;
                  const validated = reqState.filter((r) => r.ok).length;
                  const pending = docs.some((x) => x.status === "en_attente");
                  const refusedDocs = docs.filter((x) => x.status === "refuse");
                  const refusedTypes = [...new Set(refusedDocs.map((x) => x.docType))];
                  const badge = docsBadgeMeta({
                    refused: refusedDocs.length,
                    pending,
                    validated: validated >= REQUIRED_DOC_TYPES.length,
                    refusedTypes,
                  });
                  return (
                    <span
                      data-testid="dossier-docs-badge"
                      title={badge.title}
                      className={`rounded-full border px-2 py-0.5 font-mono text-[10px] ${badge.cls}`}
                    >
                      {distinct}/{REQUIRED_DOC_TYPES.length}
                    </span>
                  );
                })()}
              </h3>
              <div className="mb-4 flex gap-2">
                <select data-testid="doc-type-select" className="field-input w-auto flex-1" value={docType} onChange={(e) => setDocType(e.target.value)}>
                  <option value="">Type de document…</option>
                  {DOC_TYPES.filter((t) => t !== "Rapport COFRAC" || (openDossier.operations || []).some((o) => o.fiche === "IND-UT-102")).map((t) => (
                    <option key={t} value={t}>{t}{REQUIRED_DOC_TYPES.includes(t) ? " (obligatoire)" : ""}</option>
                  ))}
                </select>
                <label className="btn-ghost shrink-0 cursor-pointer" data-testid="doc-upload-trigger">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  Téléverser
                  <input
                    data-testid="doc-upload-input"
                    type="file" className="hidden"
                    onChange={(e) => uploadDoc(e.target.files?.[0])}
                  />
                </label>
              </div>
              <div className="space-y-2">
                {docs.length === 0 && <p className="rounded-xl border border-dashed border-[var(--line)] px-4 py-6 text-center text-xs text-[var(--mute)]">Aucun document pour ce dossier.</p>}
                {docs.map((doc) => {
                  const meta = docStatusMeta[doc.status] || docStatusMeta.en_attente;
                  const Icon = meta.icon;
                  return (
                    <div key={doc.id} data-testid={`doc-item-${doc.id}`} className="flex flex-wrap items-center gap-2 rounded-xl border border-[var(--line)] bg-[var(--inset)] px-4 py-3 text-sm">
                      <Icon size={15} className={meta.cls} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-[var(--ink)]">{doc.docType}</div>
                        <div className="truncate text-[11px] text-[var(--mute)]">{doc.filename} · {Math.round(doc.size / 1024)} Ko · {meta.label}{doc.statusComment ? ` — ${doc.statusComment}` : ""}</div>
                      </div>
                      <a data-testid={`doc-download-${doc.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/download`}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:text-[var(--accent-text)]" title="Télécharger">
                        <Download size={13} />
                      </a>
                      {isAdmin && (
                        <>
                          <button data-testid={`doc-validate-${doc.id}`} title="Valider" onClick={() => setDocStatus(doc.id, "valide")}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-sky-500/40 hover:text-[var(--accent-text)]">
                            <CheckCircle2 size={13} />
                          </button>
                          <button data-testid={`doc-refuse-${doc.id}`} title="Refuser" onClick={() => setDocStatus(doc.id, "refuse")}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-rose-500/40 hover:text-[var(--rose-text)]">
                            <XCircle size={13} />
                          </button>
                        </>
                      )}
                      <button data-testid={`doc-delete-${doc.id}`} title="Supprimer" onClick={() => deleteDoc(doc.id)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-[var(--line)] text-[var(--dim)] hover:border-rose-500/40 hover:text-[var(--rose-text)]">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Appels à facturation (admin) */}
              {isAdmin && (
                <div className="mt-8 border-t border-[var(--line)] pt-6">
                  <h3 className="font-display mb-3 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-[var(--dim)]">
                    <Receipt size={14} className="text-[var(--accent-text)]" /> Appels à facturation
                  </h3>
                  <div className="grid gap-2 sm:grid-cols-3">
                    <a data-testid="drawer-appel-dossier-btn" href={`${API_URL}/dossiers/${openDossier.id}/appel-facturation.pdf?type=dossier`} download className="btn-ghost justify-center">
                      <FileDown size={14} /> Dossier
                    </a>
                    <a data-testid="drawer-appel-commission-btn" href={`${API_URL}/dossiers/${openDossier.id}/appel-facturation.pdf?type=commission`} download className="btn-ghost justify-center">
                      <FileDown size={14} /> Commission
                    </a>
                    <a data-testid="drawer-appel-prime-btn" href={`${API_URL}/dossiers/${openDossier.id}/appel-facturation.pdf?type=prime`} download className="btn-ghost justify-center">
                      <FileDown size={14} /> Prime CEE
                    </a>
                  </div>
                  <p className="mt-2 text-[11px] text-[var(--mute)]">
                    Factures émises sous appel de GENIS : prime en remise sur devis et commission → facture adressée à GENIS (transmise par email, adresse indiquée sur l'appel à facturation) ; prime en direct → facture adressée au bénéficiaire. Mentions obligatoires : nom du dossier, Obligé, référence, volume CEE, montant (prime hors champ de TVA, commission avec TVA).
                  </p>
                  {!openDossier.oblige && (
                    <p data-testid="dossier-oblige-warning" className="mt-1.5 text-[11px] text-[var(--amber-text)]">
                      Renseignez l'Obligé ci-dessus avant d'établir la facture.
                    </p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="mt-8 flex flex-wrap gap-2.5 border-t border-[var(--line)] pt-6">
                <button data-testid="drawer-edit-btn" onClick={() => navigate(`/calculateur?edit=${openDossier.id}`)} className="btn-primary flex-1">
                  <Pencil size={14} /> Modifier
                </button>
                <a
                  data-testid="drawer-devis-pdf-btn"
                  href={`${API_URL}/dossiers/${openDossier.id}/devis.pdf`}
                  download
                  className="btn-ghost flex-1"
                  title="Télécharger le devis PDF"
                >
                  <FileDown size={14} /> Devis PDF
                </a>
                <button data-testid="drawer-compare-btn" onClick={() => addToComparateur(openDossier)} className="btn-ghost flex-1">
                  <GitCompareArrows size={14} /> Comparer
                </button>
                <button data-testid="drawer-delete-btn" onClick={() => deleteDossier(openDossier)} className="btn-ghost text-[var(--rose-text)]">
                  <Trash2 size={14} />
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

const APPLICATIONS_LABELS = { pompage: "Pompage", ventilation: "Ventilation", air_comprime: "Air comprimé", frigorifique: "Compression frigorifique", autre: "Autre application" };
const APPLICATIONS_LABEL_SAFE = (op) => (op.fiche === "IND-UT-132" ? "—" : APPLICATIONS_LABELS[op.application] || op.application);

export default Dossiers;
